import { fmtEur, payOuts, type Transaction } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

function drawerHtml(): string {
  return `
    <div class="drawer-backdrop" id="drawer-backdrop"></div>
    <aside class="drawer" id="drawer" aria-hidden="true" role="dialog" aria-labelledby="drawer-title">
      <div class="drawer-head">
        <div>
          <span class="page-eyebrow" style="margin-bottom: 0.4rem;">Pay-Out<span class="page-eyebrow-rule"></span>New instruction</span>
          <h2 id="drawer-title" class="panel-title" style="font-size: 1.25rem;">Create customer pay-out</h2>
        </div>
        <button class="icon-btn" id="drawer-close" aria-label="Close">${icon("close", 16)}</button>
      </div>
      <div class="drawer-body">
        <div class="form-grid">
          <div class="field form-grid-full">
            <label class="field-label" for="d-customer">Customer</label>
            <input class="field-control" id="d-customer" placeholder="Search customer name or wallet" />
            <span class="field-help">Returns customers with active wallets in the past 90 days.</span>
          </div>
          <div class="field">
            <label class="field-label" for="d-method">Method</label>
            <select class="field-control" id="d-method">
              <option>SEPA · IBAN</option>
              <option>Card · Gateway</option>
              <option>Crypto · USDC (Solana)</option>
              <option>Crypto · EURC (Ethereum)</option>
            </select>
          </div>
          <div class="field">
            <label class="field-label" for="d-execute">Execute</label>
            <select class="field-control" id="d-execute">
              <option>Immediately</option>
              <option>Next clearing window</option>
              <option>Schedule for…</option>
            </select>
          </div>
          <div class="field form-grid-full">
            <label class="field-label" for="d-destination">Destination</label>
            <input class="field-control" id="d-destination" value="EE38 7700 7710 0000 0417" />
            <span class="field-help">Saved beneficiaries can be picked from the list above.</span>
          </div>
          <div class="field form-grid-full">
            <label class="field-label" for="d-amount">Amount</label>
            <input class="field-control is-amount" id="d-amount" value="€ 1,200.00" />
            <span class="field-help">Available pay-out balance · €84,210.50</span>
          </div>
          <div class="field form-grid-full">
            <label class="field-label" for="d-ref">Reference</label>
            <input class="field-control" id="d-ref" placeholder="Free-text reference shown to the customer" />
          </div>
        </div>

        <div style="margin-top: 1.75rem; padding: 1.1rem 1.25rem; background: var(--surface-canvas); border: 1px solid var(--line-default); display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; font-variant-numeric: tabular-nums;">
          <div>
            <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Fee</div>
            <div style="font-weight: 600; font-size: 0.95rem;">€0.50 · SEPA</div>
          </div>
          <div>
            <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Net to customer</div>
            <div style="font-weight: 700; font-size: 1.05rem; color: var(--accent);">€1,199.50</div>
          </div>
          <div style="grid-column: 1 / -1; font-size: 0.7rem; color: var(--ink-subtle); border-top: 1px solid var(--line-subtle); padding-top: 0.75rem;">
            Cleared via SEPA Instant. Funds typically arrive in <strong>under 60 seconds</strong>; in some banks the window can be up to 2 hours.
          </div>
        </div>
      </div>
      <div class="drawer-foot">
        <button class="btn btn-ghost btn-sm" id="drawer-cancel">Cancel</button>
        <button class="btn btn-primary btn-sm">Sign & queue ${icon("arrowRight", 11)}</button>
      </div>
    </aside>
  `;
}

/**
 * Mount the create-pay-out drawer into <body> so it always overlays the
 * full viewport regardless of the current page's grid/flex context.
 *
 * Returns a teardown that removes both nodes and any global listeners.
 * The page's own teardown invokes this when navigating away, so the drawer
 * never leaks into another route.
 */
function mountDrawer(onClose?: () => void): () => void {
  const wrap = document.createElement("div");
  wrap.innerHTML = drawerHtml();
  // wrap holds two children: the backdrop and the aside.
  const backdrop = wrap.querySelector<HTMLElement>("#drawer-backdrop")!;
  const drawer = wrap.querySelector<HTMLElement>("#drawer")!;
  document.body.appendChild(backdrop);
  document.body.appendChild(drawer);

  // Open animation — apply class on next frame so transition runs.
  requestAnimationFrame(() => {
    drawer.classList.add("is-open");
    backdrop.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
  });

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    drawer.classList.remove("is-open");
    backdrop.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    // Wait for the slide-out, then yank the nodes.
    setTimeout(() => {
      backdrop.remove();
      drawer.remove();
    }, 360);
    document.removeEventListener("keydown", onKey);
    onClose?.();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }
  document.addEventListener("keydown", onKey);

  drawer
    .querySelector<HTMLElement>("#drawer-close")
    ?.addEventListener("click", close);
  drawer
    .querySelector<HTMLElement>("#drawer-cancel")
    ?.addEventListener("click", close);
  drawer
    .querySelector<HTMLElement>(".btn-primary")
    ?.addEventListener("click", close);
  backdrop.addEventListener("click", close);

  // Focus the first input for keyboard users.
  setTimeout(() => {
    drawer.querySelector<HTMLInputElement>("#d-customer")?.focus();
  }, 100);

  return close;
}

const sourceData: Transaction[] = [
  ...payOuts,
  ...Array.from({ length: 12 }, (_, i) => {
    const base = payOuts[i % payOuts.length]!;
    return {
      ...base,
      id: `PO-A-10${(94 - i).toString().padStart(2, "0")}`,
      date: i < 6 ? "05 May" : "04 May",
      time: `${(15 - i).toString().padStart(2, "0")}:${(40 - i * 4 + 60) % 60} CET`,
      amountEur: Math.round(base.amountEur * (0.5 + (i % 5) * 0.22)),
      status: i === 3 ? "pending" : i === 8 ? "failed" : "success",
      statusLabel: i === 3 ? "Pending" : i === 8 ? "Failed" : "Cleared",
    } as Transaction;
  }),
];

function row(t: Transaction): string {
  return `<div class="ledger-table-row" style="grid-template-columns: 90px 1.6fr 1fr 1fr 130px 30px;">
    <div class="col-date">
      <strong>${t.date}</strong>
      ${t.time}
    </div>
    <div class="col-name">
      <strong>${t.customer}</strong>
      <span>REF · ${t.id} · ${t.ref}</span>
    </div>
    <div class="col-method">
      <span class="col-method-icon is-${t.method === "sepa" ? "card" : t.method}">${t.method[0]?.toUpperCase()}</span>
      ${t.methodLabel}
    </div>
    <div class="col-amount is-out">
      −${fmtEur(t.amountEur)}
      ${t.amountText ? `<span class="col-amount-sub">${t.amountText}</span>` : ""}
    </div>
    <div class="col-status">
      <span class="status-seal is-${t.status}"></span> ${t.statusLabel}
    </div>
    <div class="col-chevron">→</div>
  </div>`;
}

export function renderPayOuts(ctx: Ctx) {
  const total = sourceData.reduce((a, t) => a + t.amountEur, 0);
  const pending = sourceData.filter((t) => t.status === "pending").length;
  const failed = sourceData.filter((t) => t.status === "failed").length;

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Customer Funds<span class="page-eyebrow-rule"></span>Pay-Outs</span>
        <h1 class="page-title">Outbound to customers</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">Export ${icon("download", 11)}</button>
        <button class="btn btn-primary" id="open-create">
          ${icon("plus", 11)} Create pay-out
        </button>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Total Out · 7d</span>
        <span class="summary-cell-value">${fmtEur(total, { compact: true })}</span>
        <span class="summary-cell-note">${sourceData.length} pay-outs</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Awaiting approval</span>
        <span class="summary-cell-value">${pending}</span>
        <span class="summary-cell-note is-down">Action required</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Failed</span>
        <span class="summary-cell-value">${failed}</span>
        <span class="summary-cell-note is-down">Needs review</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Avg. processing</span>
        <span class="summary-cell-value">12<small style="font-size: 0.5em; color: var(--ink-muted);">m</small></span>
        <span class="summary-cell-note">From queue → cleared</span>
      </div>
    </section>

    <section class="panel">
      <div class="filter-bar">
        <input class="filter-input" type="search" placeholder="Search customer, reference or amount" id="filter-search" />
        <select class="filter-select" id="filter-method">
          <option value="">All methods</option>
          <option value="sepa">SEPA</option>
          <option value="card">Card</option>
          <option value="crypto">Crypto</option>
        </select>
        <select class="filter-select" id="filter-status">
          <option value="">All statuses</option>
          <option value="success">Cleared</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <span class="filter-spacer"></span>
        <span class="filter-tag">${pending} pending <button aria-label="Clear">×</button></span>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 90px 1.6fr 1fr 1fr 130px 30px;">
          <span>Date / Time</span>
          <span>Customer / Destination</span>
          <span>Method</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
          <span></span>
        </div>
        <div id="payouts-rows"></div>
        <div class="ledger-table-foot">
          <span id="payouts-foot">Showing ${sourceData.length} of ${sourceData.length} entries</span>
          <span style="font-size: 0.65rem; color: var(--ink-subtle); letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700;">EUR</span>
        </div>
      </div>
    </section>
  `;

  const rowsEl = ctx.canvas.querySelector<HTMLElement>("#payouts-rows")!;
  const footEl = ctx.canvas.querySelector<HTMLElement>("#payouts-foot")!;
  const search = ctx.canvas.querySelector<HTMLInputElement>("#filter-search")!;
  const methodSel =
    ctx.canvas.querySelector<HTMLSelectElement>("#filter-method")!;
  const statusSel =
    ctx.canvas.querySelector<HTMLSelectElement>("#filter-status")!;

  function applyFilter() {
    const q = search.value.toLowerCase().trim();
    const m = methodSel.value;
    const s = statusSel.value;
    const filtered = sourceData.filter((t) => {
      if (m && t.method !== m) return false;
      if (s && t.status !== s) return false;
      if (q) {
        const hay =
          `${t.customer} ${t.id} ${t.ref} ${t.amountEur}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rowsEl.innerHTML = filtered.length
      ? filtered.map(row).join("")
      : `<div style="padding: 3rem; text-align: center; color: var(--ink-muted); font-size: 0.85rem;">No pay-outs match your filters.</div>`;
    footEl.textContent = `Showing ${filtered.length} of ${sourceData.length} entries`;
  }

  [search, methodSel, statusSel].forEach((el) =>
    el.addEventListener("input", applyFilter),
  );
  applyFilter();

  // Drawer — portal-mounted to <body> so it overlays everything cleanly.
  // We track the active close fn so navigation can yank it.
  let activeClose: (() => void) | null = null;
  ctx.canvas
    .querySelector<HTMLElement>("#open-create")
    ?.addEventListener("click", () => {
      if (activeClose) return; // already open
      activeClose = mountDrawer(() => {
        activeClose = null;
      });
    });

  return () => {
    activeClose?.();
  };
}
